<script setup>
import { computed, ref } from 'vue';
import * as logger from '../../../utils/logger';
import AppBadge from '../../atoms/app/AppBadge.vue';
import AppLink from '../../atoms/app/AppLink.vue';
import AppIcon from '../../atoms/app/AppIcon.vue';
import { useBreakpoints } from '../../../composables/breakpoint';
import { addJobWatchlist, removeJobWatchlist } from '../../../services/jobWatchlist';
import { useAuth } from '../../../stores/auth-store';
import SmartBewerbenLogo from '../../../assets/img/logos/smartBewerbenPill.svg';
import FallbackCompanyLogo from '../../../assets/img/logos/fallbackCompanyLogo.svg';
import { getHumanizedDateDifference } from '../../../utils/date';

const props = defineProps({
    job: {
        type: Object,
    },
});

const { isLargeScreen } = useBreakpoints();

const jobDate = computed(() => getHumanizedDateDifference(props.job.date));

/* Watchlist functionality */
const user = useAuth();

const showWatchlistButton = computed(() => user.isLoggedIn);
const isWatched = ref(props.job.watchlist.watched);
const watchlistId = ref(props.job.watchlist.id);
const toggleWatchlist = async () => {
    if (isWatched.value) {
        try {
            await removeJobWatchlist(props.job.watchlist.id);
            isWatched.value = false;
        } catch (exception) {
            logger.error({
                exception,
                fingerprint: ['Removing job from watchlist failed'],
                message: 'Removing job from watchlist failed.',
            });
        }
    } else {
        try {
            const { data } = await addJobWatchlist(props.job.id);
            isWatched.value = true;
            watchlistId.value = data.id;
        } catch (exception) {
            logger.error({
                exception,
                fingerprint: ['Adding job to watchlist failed'],
                message: 'Adding job to watchlist failed.',
            });
        }
    }
};

</script>

<template>
    <div
        class="c-jobCard p-l h-full rounded-md border border-solid border-gray-900 bg-white overflow-hidden"
        data-qa="job card"
    >
        <div class="flex gap-l">
            <div class="c-jobCard__leftSide">
                <a
                    :href="job.company?.link"
                    class="block flex-shrink-0 mb-s"
                    data-qa="job card company link"
                    :aria-label="`Mehr erfahren über Firma ${job.company?.name}`"
                >
                    <div class="w-4xl aspect-square k-c-imageBox">
                        <img
                            v-if="job.company?.logo?.companysquare"
                            :src="job.company?.logo?.companysquare"
                            alt="Firmenlogo"
                        >
                        <FallbackCompanyLogo
                            v-else
                            class="w-full aspect-square text-gray-300 p-[5.5px]"
                        />
                    </div>
                </a>
            </div>
            <div class="c-jobCard__content flex-grow">
                <div class="flex gap-l flex-grow justify-between">
                    <div>
                        <div>
                            <div class="flex gap-xs has-[.c-appBadge]:mb-xs">
                                <AppBadge smart>
                                    <SmartBewerbenLogo class="h-l"/>
                                </AppBadge>
                                <AppBadge
                                    v-if="job.company?.isBrandingSolution || job.company?.isEmployerPagePlus"
                                    condensed
                                    primary
                                    full
                                    data-qa="bs badge"
                                >
                                    EINBLICKE
                                </AppBadge>

                                <AppBadge
                                    v-if="job.company?.isBrandingSolution"
                                    condensed
                                    secondary
                                    full
                                    data-qa="video badge"
                                >
                                    VIDEOS
                                </AppBadge>
                            </div>
                            <h2 class="inline font-semibold">
                                <AppLink
                                    neutral
                                    :href="job.link"
                                >
                                    {{ job.title }}
                                </AppLink>
                            </h2>
                        </div>
                        <div class="c-jobCard__company">
                            <AppLink
                                v-if="job.company?.link"
                                :href="job.company?.link"
                                class="break-words hyphens-auto"
                                data-qa="company link"
                            >
                                {{ job.company?.name }}
                            </AppLink>
                            <span
                                v-else
                                class="text-gray-500"
                            >
                                {{ job.company?.name }}
                            </span>
                        </div>
                    </div>
                    <div
                        v-if="showWatchlistButton && !isLargeScreen"
                        class="flex-shrink-0"
                    >
                        <AppLink
                            tag="button"
                            class="flex items-center gap-s"
                            :data-qa="`watchlist toggle - ${isWatched ? 'active' : 'inactive'}`"
                            :aria-label="isWatched
                                ? `${job.title} bei ${job.company?.name} von deiner Merkliste entfernen`
                                : `${job.title} bei ${job.company?.name} auf deine Merkliste setzen`"
                            @click="toggleWatchlist"
                        >
                            <AppIcon
                                size="xl"
                                :name="isWatched ? 'star-filled' : 'star'"
                            />
                            <span class="hidden m:inline">
                                {{ isWatched ? 'Gemerkt' : 'Merken' }}
                            </span>
                        </AppLink>
                    </div>
                </div>
                <span>
                    {{ jobDate }}
                    <template v-if="job.pushedJob">&middot; Gesponsert</template>
                </span>
                <AppLink
                    v-if="showWatchlistButton && isLargeScreen"
                    tag="button"
                    class="flex items-center gap-s mt-m"
                    :data-qa="`watchlist toggle - ${isWatched ? 'active' : 'inactive'}`"
                    :aria-label="isWatched
                        ? `${job.title} bei ${job.company?.name} von deiner Merkliste entfernen`
                        : `${job.title} bei ${job.company?.name} auf deine Merkliste setzen`"
                    @click="toggleWatchlist"
                >
                    <AppIcon
                        size="xl"
                        :name="isWatched ? 'star-filled' : 'star'"
                    />
                    {{ isWatched ? 'Gemerkt' : 'Merken' }}
                </AppLink>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/tools/typo';
@use '~@karriereat/global-styles/scss/components/button/index' as button;
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/components/image-box/index' as imageBox;

.c-jobCard {
    transition: transform 0.3s ease-out;

    &:hover {
        box-shadow: 5px 5px 0 0 var(--k-gray-900);
        transform: translateY(-5px);
    }
}
</style>
